import { Button, ConfigProvider, DatePicker, Input, InputNumber, Modal, Segmented } from 'antd';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FlowAnalysis } from './FlowAnalysis';
import { AlarmAnalysis } from './AlarmAnalysis';
import { TransmissionAnalysis } from './TransmissionAnalysis';
import { ConsumptionAnalysis } from './ConsumptionAnalysis';
import dayjs from 'dayjs';
import {
  getSystemAnalysisAlarmExcelData,
  getSystemAnalysisConsumption,
  getSystemAnalysisConsumptionExcelData,
  getSystemAnalysisFlow,
  getSystemAnalysisFlowExcelData,
  getSystemAnalysisIndividualConsumption,
  getSystemAnalysisIndividualConsumptionExcelData,
  getSystemAnalysisIndividualFlow,
  getSystemAnalysisIndividualFlowExcelData,
  getSystemAnalysisIndividualMeterAlarm,
  getSystemAnalysisIndividualMeterAlarmExcelData,
  getSystemAnalysisIndividualTransmission,
  getSystemAnalysisIndividualTransmissionExcelData,
  getSystemAnalysisMeterAlarm,
  getSystemAnalysisTransmission,
  getSystemAnalysisTransmissionExcelData,
} from '../../../Services/systemAnalysis';
import { ExcelFileGeneratorUtil } from '../../../utils/ExcelUtils/ExcelFileGeneratorUtil';
import {
  alarmSystemAnalysisExcelProcess,
  genericSystemAnalysisExcelProcess,
} from './SystemAnalysisExcelProcess';
import {
  SET_CONSUMPTION_LIMIT_MAX,
  SET_SINGLE_METER_VALUE,
  SET_VIEW_SINGLE_METER,
} from '../../../store/features/systemAnalysisSlice';
import { useDispatch, useSelector } from 'react-redux';
import {
  SET_IS_API_CALLED,
  SET_IS_API_CALLED_FINISHED,
} from '../../../store/features/loadingSlice';

function getDefaultMeterAnalysisDateRange() {
  return {
    dateTo: dayjs().subtract(1, 'day').format('YYYY-MM-DDTHH:mm:ss').toString(),
    dateFrom: dayjs().subtract(1, 'month').format('YYYY-MM-DDTHH:mm:ss').toString(),
  };
}

const SystemMeterAnalysisPage = () => {
  const dispatch = useDispatch();
  const [dateWarning, setDateWarning] = useState(false);
  const { viewSingleMeter, singleMeterValue } = useSelector((state) => state.SystemAnalysis);
  const [dateValue, setDateValue] = useState(() => getDefaultMeterAnalysisDateRange());
  const [showTable, setShowTable] = useState(false);
  const [analysisContentType, setAnalysisContentType] = useState('alarm');
  const [analysisData, setAnalysisData] = useState({});
  const flowHadSingleMeterRef = useRef(false);
  const flowNeedsDefaultRangeRef = useRef(false);

  const hasSerialInFilter = () =>
    singleMeterValue !== '' && singleMeterValue !== undefined && singleMeterValue !== null;

  const getFlowSingleMeterTodayRange = () => ({
    dateFrom: dayjs()
      .startOf('day')
      .add(dayjs().utcOffset(), 'minute')
      .format('YYYY-MM-DDTHH:mm:ss'),
    dateTo: dayjs()
      .endOf('day')
      .add(dayjs().utcOffset(), 'minute')
      .format('YYYY-MM-DDTHH:mm:ss'),
  });

  const getEffectiveDateValueForRequest = () => {
    if (analysisContentType !== 'flow') {
      return dateValue;
    }
    const serialActive = viewSingleMeter && hasSerialInFilter();
    if (serialActive) {
      return getFlowSingleMeterTodayRange();
    }
    if (flowNeedsDefaultRangeRef.current || flowHadSingleMeterRef.current) {
      return getDefaultMeterAnalysisDateRange();
    }
    return dateValue;
  };

  const analysisRequestGenerationRef = useRef(0);

  const analysisContentApiCallMap = {
    alarm: {
      apiCall: getSystemAnalysisMeterAlarm,
      singleMeterApiCall: getSystemAnalysisIndividualMeterAlarm,
      excelApiCall: getSystemAnalysisAlarmExcelData,
      singleMeterExcelApiCall: getSystemAnalysisIndividualMeterAlarmExcelData,
      excelDataProcess: alarmSystemAnalysisExcelProcess,
      excelFileName: 'system-analysis-alarm.xlsx',
    },
    transmission: {
      apiCall: getSystemAnalysisTransmission,
      singleMeterApiCall: getSystemAnalysisIndividualTransmission,
      excelApiCall: getSystemAnalysisTransmissionExcelData,
      singleMeterExcelApiCall: getSystemAnalysisIndividualTransmissionExcelData,
      excelDataProcess: genericSystemAnalysisExcelProcess,
      excelFileName: 'system-analysis-transmission.xlsx',
    },
    consumption: {
      apiCall: getSystemAnalysisConsumption,
      singleMeterApiCall: getSystemAnalysisIndividualConsumption,
      excelApiCall: getSystemAnalysisConsumptionExcelData,
      singleMeterExcelApiCall: getSystemAnalysisIndividualConsumptionExcelData,
      excelDataProcess: genericSystemAnalysisExcelProcess,
      excelFileName: 'system-analysis-consumption.xlsx',
    },
    flow: {
      apiCall: getSystemAnalysisFlow,
      singleMeterApiCall: getSystemAnalysisIndividualFlow,
      singleMeterExcelApiCall: getSystemAnalysisIndividualFlowExcelData,
      excelApiCall: getSystemAnalysisFlowExcelData,
      excelDataProcess: genericSystemAnalysisExcelProcess,
      excelFileName: 'system-analysis-flow.xlsx',
    },
  };

  useLayoutEffect(() => {
    if (analysisContentType !== 'flow') return;

    const serialActive = viewSingleMeter && hasSerialInFilter();

    if (serialActive) {
      flowHadSingleMeterRef.current = true;
      flowNeedsDefaultRangeRef.current = false;
      const next = getFlowSingleMeterTodayRange();
      setDateValue((prev) =>
        prev.dateFrom === next.dateFrom && prev.dateTo === next.dateTo ? prev : next
      );
      return;
    }

    if (flowHadSingleMeterRef.current) {
      flowNeedsDefaultRangeRef.current = true;
      flowHadSingleMeterRef.current = false;
      const def = getDefaultMeterAnalysisDateRange();
      setDateValue((prev) =>
        prev.dateFrom === def.dateFrom && prev.dateTo === def.dateTo ? prev : def
      );
    }
  }, [viewSingleMeter, analysisContentType, singleMeterValue]);

  useEffect(() => {
    getAnalysisData();
  }, [dateValue, analysisContentType, singleMeterValue, viewSingleMeter]);

  const getAnalysisData = () => {
    const apiCall = analysisContentApiCallMap[analysisContentType];

    viewSingleMeter
      ? handleEmptySingleMeterValue(() => consumeSingleMeterApiCall(apiCall?.singleMeterApiCall))
      : consumeApiCall(apiCall?.apiCall);
  };

  const handleEmptySingleMeterValue = (consumeSingleMeterApiCall) => {
    if (singleMeterValue === '' || singleMeterValue === undefined || singleMeterValue === null) {
      dispatch(SET_VIEW_SINGLE_METER(false));
    } else {
      consumeSingleMeterApiCall();
    }
  };

  const consumeApiCall = (apiCall) => {
    if (!apiCall) return;
    const gen = ++analysisRequestGenerationRef.current;
    const payload = getEffectiveDateValueForRequest();
    flowNeedsDefaultRangeRef.current = false;
    dispatch(SET_IS_API_CALLED('Fetching Analysis Data'));
    apiCall(payload)
      .then((res) => {
        if (gen !== analysisRequestGenerationRef.current) return;
        setAnalysisData(res?.data ?? {});
      })
      .finally(() => {
        if (gen === analysisRequestGenerationRef.current) {
          dispatch(SET_IS_API_CALLED_FINISHED());
        }
      });
  };

  const consumeSingleMeterApiCall = (apiCall) => {
    if (!apiCall) return;
    const gen = ++analysisRequestGenerationRef.current;
    const payload = getEffectiveDateValueForRequest();
    flowNeedsDefaultRangeRef.current = false;
    dispatch(SET_IS_API_CALLED('Fetching Analysis Data'));

    apiCall(singleMeterValue, payload)
      .then((res) => {
        if (gen !== analysisRequestGenerationRef.current) return;
        setAnalysisData(res?.data ?? {});
      })
      .finally(() => {
        if (gen === analysisRequestGenerationRef.current) {
          dispatch(SET_IS_API_CALLED_FINISHED());
        }
      });
  };

  const callExcelApi = () => {
    if (dateWarning === true) {
      Modal.error({
        title: 'Date Error',
        content:
          'Please select a starting date and an ending date before trying to download the excel file',
      });
    } else {
      const apiCall = analysisContentApiCallMap[analysisContentType];
      !viewSingleMeter
        ? consumeExcelApiCall(
            apiCall?.excelApiCall,
            apiCall?.excelFileName,
            apiCall?.excelDataProcess
          )
        : consumeSingleMeterExcelApiCall(
            apiCall?.singleMeterExcelApiCall,
            apiCall?.excelFileName,
            apiCall?.excelDataProcess
          );
    }
  };

  const consumeExcelApiCall = (
    apiCall,
    excelFileName = 'system-analysis-alarm.xlsx',
    excelDataProcess
  ) => {
    dispatch(SET_IS_API_CALLED('Fetching Analysis Excel Data'));
    const excelPayload = getEffectiveDateValueForRequest();
    flowNeedsDefaultRangeRef.current = false;
    apiCall(excelPayload)
      .then((res) => {
        const formattedData = excelDataProcess(res?.data);
        ExcelFileGeneratorUtil({
          filename: excelFileName,
          formattedDataForExcelFile: formattedData,
        });
      })
      .finally(() => {
        dispatch(SET_IS_API_CALLED_FINISHED());
      });
  };

  const consumeSingleMeterExcelApiCall = (
    apiCall,
    excelFileName = 'system-analysis-alarm.xlsx',
    excelDataProcess
  ) => {
    dispatch(SET_IS_API_CALLED('Fetching Analysis Excel Data'));
    const excelPayload = getEffectiveDateValueForRequest();
    flowNeedsDefaultRangeRef.current = false;
    apiCall(singleMeterValue, excelPayload)
      .then((res) => {
        const formattedData = excelDataProcess(res?.data);
        ExcelFileGeneratorUtil({
          filename: excelFileName,
          formattedDataForExcelFile: formattedData,
        });
      })
      .finally(() => {
        dispatch(SET_IS_API_CALLED_FINISHED());
      });
  };

  return (
    <>
      <div className={'p-4'}>
        <MeterAnalysisHeader
          setAnalysisContentType={setAnalysisContentType}
          analysisContentType={analysisContentType}
        />
        <MeterAnalysisFilters
          setShowTable={setShowTable}
          showTable={showTable}
          dateValue={dateValue}
          setDateValue={setDateValue}
          callExcelApi={callExcelApi}
          setDateWarning={setDateWarning}
          analysisContentType={analysisContentType}
        />
        <AnalysisContentTypeSwitcher
          analysisContentType={analysisContentType}
          showTable={showTable}
          analysisData={analysisData}
        />
      </div>
    </>
  );
};

// TODO need to change the colours for the charts
const AnalysisContentTypeSwitcher = ({
  analysisContentType = 'alarm',
  showTable = false,
  analysisData = {},
}) => {
  const contentMap = [
    {
      contentType: 'alarm',
      contentComponent: (
        <AlarmAnalysis key={'alarm-analysis-1'} showTable={showTable} analysisData={analysisData} />
      ),
    },
    {
      contentType: 'transmission',
      contentComponent: (
        <TransmissionAnalysis
          key={'transmission-analysis-1'}
          showTable={showTable}
          analysisData={analysisData}
        />
      ),
    },
    {
      contentType: 'consumption',
      contentComponent: (
        <ConsumptionAnalysis
          key={'consumption-analysis-1'}
          showTable={showTable}
          analysisData={analysisData}
        />
      ),
    },
    {
      contentType: 'flow',
      contentComponent: (
        <FlowAnalysis key={'flow-analysis-1'} showTable={showTable} analysisData={analysisData} />
      ),
    },
  ];

  return (
    <>
      {contentMap
        ?.filter((content) => content.contentType === analysisContentType)
        ?.map((content) => content.contentComponent)}
    </>
  );
};

const MeterAnalysisHeader = ({ setAnalysisContentType, analysisContentType }) => {
  const navigate = useNavigate();

  useEffect(() => {}, []);

  return (
    <>
      <div className="pb-2 text-lg font-bold">
        <p>Meter Analysis</p>
      </div>
      <div className="alarm-analysis-header mb-6 grid grid-cols-3 items-center">
        <div className="col-start-2 flex justify-center">
          <ConfigProvider
            theme={{
              components: {
                Segmented: {
                  /* here is your component tokens */
                  itemSelectedBg: '#2F5E8A',
                  itemSelectedColor: '#F5F5F5',
                },
              },
            }}
          >
            <Segmented
              size="large"
              options={[
                { label: 'Alarm', value: 'alarm' },
                {
                  label: 'Transmission',
                  value: 'transmission',
                },
                { label: 'Consumption', value: 'consumption' },
                { label: 'Flow', value: 'flow' },
              ]}
              onChange={(value) => {
                setAnalysisContentType(value);
              }}
              defaultValue={analysisContentType}
              className="w-100 rounded-md border-2 px-6 py-1"
            />
          </ConfigProvider>
        </div>
        <div className="grid w-full grid-cols-3 gap-4">
          <div className="col-start-2 flex">
            <Button
              className="basis-full bg-medium-blue-100 text-white"
              onClick={() => navigate('/wf-create-case')}
            >
              Create Case
            </Button>
          </div>
          <div className="col-start-3 flex">
            <Button
              className="basis-full bg-medium-blue-100 px-4 text-white"
              onClick={() => navigate('/wf-technician-listing')}
            >
              Forward to Case
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

const MeterAnalysisFilters = ({
  setShowTable,
  showTable,
  dateValue,
  setDateValue,
  callExcelApi,
  setDateWarning,
  analysisContentType,
}) => {
  const dispatch = useDispatch();
  const { RangePicker } = DatePicker;
  const [meterSerialNumber, setMeterSerialNumber] = useState('');
  const { singleMeterValue } = useSelector((state) => state.SystemAnalysis);

  return (
    <>
      <div className="alarm-analysis-filters mb-8 grid grid-cols-3 items-center">
        <div className="flex gap-4">
          <>
            <Input
              allowClear
              className="basis-4/12"
              onChange={(evt) => {
                // dispatch(SET_SINGLE_METER_VALUE(evt.target.value))
                setMeterSerialNumber(evt.target.value);
              }}
              value={meterSerialNumber !== '' ? meterSerialNumber : singleMeterValue}
              onClear={() => {
                dispatch(SET_SINGLE_METER_VALUE(''));
                dispatch(SET_VIEW_SINGLE_METER(false));
              }}
            />

            <button
              onClick={() => {
                dispatch(SET_SINGLE_METER_VALUE(meterSerialNumber));
                dispatch(SET_VIEW_SINGLE_METER(true));
              }}
            >
              <img src="/assets/search-alt-1-svgrepo-com.svg" alt="Search" />
            </button>
          </>
        </div>
        <div className="flex justify-center gap-4">
          <RangePicker
            // allowClear
            className=""
            value={[dayjs(dateValue.dateFrom), dayjs(dateValue.dateTo)]}
            suffixIcon={
              <div className="h-3 w-3">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 22 25" fill="none">
                  <path
                    id="Icon awesome-calendar-alt"
                    d="M-0.00976562 22.558C-0.00976562 23.1587 0.228868 23.7348 0.653638 24.1596C1.07841 24.5844 1.65452 24.823 2.25523 24.823H18.8612C19.462 24.823 20.0381 24.5844 20.4628 24.1596C20.8876 23.7348 21.1262 23.1587 21.1262 22.558V9.72499H-0.00976562V22.558ZM15.0902 13.311C15.0908 13.161 15.1506 13.0174 15.2566 12.9114C15.3626 12.8053 15.5063 12.7455 15.6562 12.745H17.5402C17.6902 12.7455 17.8338 12.8053 17.9399 12.9114C18.0459 13.0174 18.1057 13.161 18.1062 13.311V15.198C18.1057 15.3479 18.0459 15.4916 17.9399 15.5976C17.8338 15.7037 17.6902 15.7635 17.5402 15.764H15.6532C15.5033 15.7635 15.3596 15.7037 15.2536 15.5976C15.1476 15.4916 15.0878 15.3479 15.0872 15.198L15.0902 13.311ZM15.0902 19.35C15.0908 19.2 15.1506 19.0564 15.2566 18.9504C15.3626 18.8443 15.5063 18.7845 15.6562 18.784H17.5402C17.6902 18.7845 17.8338 18.8443 17.9399 18.9504C18.0459 19.0564 18.1057 19.2 18.1062 19.35V21.237C18.1057 21.3869 18.0459 21.5306 17.9399 21.6366C17.8338 21.7427 17.6902 21.8025 17.5402 21.803H15.6532C15.5033 21.8025 15.3596 21.7427 15.2536 21.6366C15.1476 21.5306 15.0878 21.3869 15.0872 21.237L15.0902 19.35ZM9.04823 13.311C9.04876 13.161 9.10856 13.0174 9.21459 12.9114C9.32063 12.8053 9.46428 12.7455 9.61423 12.745H11.5012C11.6512 12.7455 11.7948 12.8053 11.9009 12.9114C12.0069 13.0174 12.0667 13.161 12.0672 13.311V15.198C12.0667 15.3479 12.0069 15.4916 11.9009 15.5976C11.7948 15.7037 11.6512 15.7635 11.5012 15.764H9.61423C9.46428 15.7635 9.32063 15.7037 9.21459 15.5976C9.10856 15.4916 9.04876 15.3479 9.04823 15.198V13.311ZM9.04823 19.35C9.04876 19.2 9.10856 19.0564 9.21459 18.9504C9.32063 18.8443 9.46428 18.7845 9.61423 18.784H11.5012C11.6512 18.7845 11.7948 18.8443 11.9009 18.9504C12.0069 19.0564 12.0667 19.2 12.0672 19.35V21.237C12.0667 21.3869 12.0069 21.5306 11.9009 21.6366C11.7948 21.7427 11.6512 21.8025 11.5012 21.803H9.61423C9.46428 21.8025 9.32063 21.7427 9.21459 21.6366C9.10856 21.5306 9.04876 21.3869 9.04823 21.237V19.35ZM3.00923 13.311C3.00976 13.161 3.06956 13.0174 3.17559 12.9114C3.28163 12.8053 3.42528 12.7455 3.57523 12.745H5.46323C5.61318 12.7455 5.75684 12.8053 5.86287 12.9114C5.96891 13.0174 6.02871 13.161 6.02923 13.311V15.198C6.02871 15.3479 5.96891 15.4916 5.86287 15.5976C5.75684 15.7037 5.61318 15.7635 5.46323 15.764H3.57623C3.42628 15.7635 3.28263 15.7037 3.17659 15.5976C3.07056 15.4916 3.01076 15.3479 3.01023 15.198L3.00923 13.311ZM3.00923 19.35C3.00976 19.2 3.06956 19.0564 3.17559 18.9504C3.28163 18.8443 3.42528 18.7845 3.57523 18.784H5.46323C5.61318 18.7845 5.75684 18.8443 5.86287 18.9504C5.96891 19.0564 6.02871 19.2 6.02923 19.35V21.237C6.02871 21.3869 5.96891 21.5306 5.86287 21.6366C5.75684 21.7427 5.61318 21.8025 5.46323 21.803H3.57623C3.42628 21.8025 3.28263 21.7427 3.17659 21.6366C3.07056 21.5306 3.01076 21.3869 3.01023 21.237L3.00923 19.35ZM18.8612 3.68599H16.5972V1.42199C16.5967 1.22192 16.517 1.03019 16.3755 0.88871C16.234 0.747234 16.0423 0.66752 15.8422 0.666992H14.3322C14.1322 0.66752 13.9404 0.747234 13.799 0.88871C13.6575 1.03019 13.5778 1.22192 13.5772 1.42199V3.68599H7.53823V1.42199C7.53771 1.22209 7.45813 1.03051 7.31687 0.889061C7.17561 0.747614 6.98414 0.667784 6.78423 0.666992L5.27423 0.666992C5.07416 0.66752 4.88243 0.747234 4.74095 0.88871C4.59948 1.03019 4.51976 1.22192 4.51923 1.42199V3.68599H2.25523C1.65452 3.68599 1.07841 3.92463 0.653638 4.3494C0.228868 4.77417 -0.00976562 5.35028 -0.00976562 5.95099L-0.00976562 8.21499H21.1262V5.95099C21.1262 5.35028 20.8876 4.77417 20.4628 4.3494C20.0381 3.92463 19.462 3.68599 18.8612 3.68599Z"
                    fill="#9497A9"
                  />
                </svg>
              </div>
            }
            format={(value) => `${value.format('DD/MM/YYYY')}`}
            onChange={(dateValue) => {
              if (dateValue !== null && dateValue !== undefined) {
                setDateWarning(false);
                setDateValue({
                  dateFrom: dayjs(dateValue[0].$d)
                    .add(dayjs().utcOffset(), 'minute')
                    .format('YYYY-MM-DDTHH:mm:ss')
                    .toString(),
                  dateTo: dayjs(dateValue[1].$d)
                    .add(dayjs().utcOffset(), 'minute')
                    .format('YYYY-MM-DDTHH:mm:ss')
                    .toString(),
                });
              } else {
                setDateWarning(true);
              }
            }}
          />
          <div className={`rounded-lg p-1 ${showTable && 'bg-dark-blue-200'}`}>
            <img
              src="/assets/table-list-svgrepo-com.svg"
              className={`h-full ${showTable && ' brightness-[500] '}`}
              alt=""
              onClick={() => {
                setShowTable(!showTable);
              }}
            />
          </div>
        </div>
        <div className="flex justify-self-end">
          <div className={'flex justify-between gap-8'}>
            {analysisContentType === 'consumption' && (
              <InputNumber
                className={'h-full'}
                defaultValue={30}
                onChange={(val) => {
                  dispatch(SET_CONSUMPTION_LIMIT_MAX(val));
                }}
              />
            )}
            <button className="h-full">
              <img
                src={'/assets/icon-excel.svg'}
                className="cursor-pointer"
                onClick={callExcelApi}
                width={30} // is it a good idea to hardcode these values?
                height={30}
                alt=""
              />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SystemMeterAnalysisPage;
